import { useState, useRef, useEffect } from 'react';
import { useTeam } from '../contexts/TeamContext';

export default function TeamSelector() {
	const { teams, currentTeam, switchTeam, isLoading } = useTeam();
	const [isOpen, setIsOpen] = useState(false);
	const [switching, setSwitching] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Handle team switch
	const handleSwitchTeam = async (teamId: string) => {
		if (teamId === currentTeam?.team_id || switching) return;

		setSwitching(true);
		try {
			await switchTeam(teamId);
			setIsOpen(false);
		} catch (error) {
			console.error('Failed to switch team:', error);
		} finally {
			setSwitching(false);
		}
	};

	// Get license badge color and text
	const getLicenseBadge = (license: any) => {
		if (license.type === 'trial') {
			const days = license.days_remaining ?? 0;
			return {
				text: `Trial: ${days}d`,
				color: days > 3 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
			};
		} else if (license.type === 'active') {
			return {
				text: 'Active',
				color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
			};
		} else if (license.type === 'expired') {
			return {
				text: 'Expired',
				color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
			};
		}
		return {
			text: 'Unknown',
			color: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
		};
	};

	if (isLoading || !currentTeam) {
		return null;
	}

	// Don't show selector if only one team
	if (teams.length <= 1) {
		const badge = getLicenseBadge(currentTeam.license);
		return (
			<div className="flex items-center space-x-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
				<div className="flex items-center space-x-2">
					<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
					</svg>
					<span className="text-sm font-medium text-slate-900 dark:text-white">{currentTeam.team_name}</span>
				</div>
				<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
					{badge.text}
				</span>
			</div>
		);
	}

	const currentBadge = getLicenseBadge(currentTeam.license);

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				disabled={switching}
				className="flex items-center space-x-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
			>
				<div className="flex items-center space-x-2">
					<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
					</svg>
					<span className="text-sm font-medium text-slate-900 dark:text-white">{currentTeam.team_name}</span>
				</div>
				<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${currentBadge.color}`}>
					{currentBadge.text}
				</span>
				{switching ? (
					<div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
				) : (
					<svg className={`w-4 h-4 text-slate-600 dark:text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				)}
			</button>

			{isOpen && (
				<div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
					<div className="p-2 border-b border-slate-200 dark:border-slate-700">
						<p className="text-xs font-medium text-slate-500 dark:text-slate-400 px-2">Switch Team</p>
					</div>
					<div className="max-h-64 overflow-y-auto">
						{teams.map((team) => {
							const badge = getLicenseBadge(team.license);
							const isCurrent = team.team_id === currentTeam.team_id;

							return (
								<button
									key={team.team_id}
									onClick={() => handleSwitchTeam(team.team_id)}
									disabled={isCurrent || switching}
									className={`w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
										isCurrent ? 'bg-slate-50 dark:bg-slate-700' : ''
									}`}
								>
									<div className="flex items-center space-x-3 flex-1 min-w-0">
										<svg className="w-5 h-5 text-slate-600 dark:text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
										</svg>
										<div className="flex-1 min-w-0 text-left">
											<p className="text-sm font-medium text-slate-900 dark:text-white truncate">
												{team.team_name}
											</p>
											<p className="text-xs text-slate-500 dark:text-slate-400">
												{team.role === 'owner' ? 'Owner' : 'Member'}
											</p>
										</div>
									</div>
									<div className="flex items-center space-x-2 flex-shrink-0">
										<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
											{badge.text}
										</span>
										{isCurrent && (
											<svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
											</svg>
										)}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
